import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Moon, Sun, Clock, AlertTriangle, Activity } from 'lucide-react';

interface SleepMetrics {
  date: string;
  totalSleep: number;
  deepSleep: number;
  lightSleep: number;
  awakeTime: number;
  restlessness: number;
  nightTimeActivity: number;
}

const SleepAnalysis: React.FC = () => {
  // Mock data - in real app this would come from floor sensors
  const sleepData: SleepMetrics[] = [
    { date: 'Mon', totalSleep: 7.5, deepSleep: 2.5, lightSleep: 5, awakeTime: 0.5, restlessness: 15, nightTimeActivity: 2 },
    { date: 'Tue', totalSleep: 6.8, deepSleep: 2.2, lightSleep: 4.6, awakeTime: 0.8, restlessness: 22, nightTimeActivity: 3 },
    { date: 'Wed', totalSleep: 7.2, deepSleep: 2.8, lightSleep: 4.4, awakeTime: 0.4, restlessness: 12, nightTimeActivity: 1 },
    { date: 'Thu', totalSleep: 6.5, deepSleep: 2.0, lightSleep: 4.5, awakeTime: 1.0, restlessness: 28, nightTimeActivity: 4 },
    { date: 'Fri', totalSleep: 7.8, deepSleep: 3.0, lightSleep: 4.8, awakeTime: 0.3, restlessness: 10, nightTimeActivity: 1 },
    { date: 'Sat', totalSleep: 8.0, deepSleep: 3.2, lightSleep: 4.8, awakeTime: 0.2, restlessness: 8, nightTimeActivity: 1 },
    { date: 'Sun', totalSleep: 7.4, deepSleep: 2.6, lightSleep: 4.8, awakeTime: 0.6, restlessness: 18, nightTimeActivity: 2 }
  ];

  const currentMetrics = {
    averageSleep: 7.3,
    sleepQuality: 85,
    bedtime: '10:30 PM',
    wakeTime: '6:45 AM',
    nightlyAwakenings: 2,
    trends: {
      totalSleep: '+0.5h',
      quality: '+5%',
      consistency: 'Improving'
    }
  };

  const getQualityColor = (quality: number) => {
    if (quality >= 85) return 'text-green-500';
    if (quality >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Sleep Analysis</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Moon className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Total Sleep</h3>
          </div>
          <p className="text-3xl font-bold">{currentMetrics.averageSleep}h</p>
          <p className="text-sm text-gray-500">Trend: {currentMetrics.trends.totalSleep}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Sleep Quality</h3>
          </div>
          <p className={`text-3xl font-bold ${getQualityColor(currentMetrics.sleepQuality)}`}>
            {currentMetrics.sleepQuality}%
          </p>
          <p className="text-sm text-gray-500">Trend: {currentMetrics.trends.quality}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Sleep Schedule</h3>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">Bed</p>
              <p className="font-medium">{currentMetrics.bedtime}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Wake</p>
              <p className="font-medium">{currentMetrics.wakeTime}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Night Awakenings</h3>
          </div>
          <p className="text-3xl font-bold">{currentMetrics.nightlyAwakenings}</p>
          <p className="text-sm text-gray-500">Average per night</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Weekly Sleep Pattern</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sleepData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="deepSleep" stackId="sleep" name="Deep Sleep" fill="#4f46e5" />
              <Bar dataKey="lightSleep" stackId="sleep" name="Light Sleep" fill="#818cf8" />
              <Bar dataKey="awakeTime" stackId="sleep" name="Awake Time" fill="#e5e7eb" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Nighttime Activity</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sleepData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="restlessness" 
                name="Restlessness" 
                stroke="#f59e0b"
                strokeWidth={2}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="nightTimeActivity" 
                name="Night Activity" 
                stroke="#ef4444"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Sleep Insights</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Clock className="text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium">Consistent Sleep Schedule</h4>
              <p className="text-gray-600">Regular bedtime between 10:00 PM and 10:30 PM shows good sleep hygiene.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="text-yellow-500" />
            </div>
            <div>
              <h4 className="font-medium">Night Awakenings</h4>
              <p className="text-gray-600">Slight increase in nighttime activity. Consider reviewing evening routine.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Activity className="text-green-500" />
            </div>
            <div>
              <h4 className="font-medium">Sleep Quality Improving</h4>
              <p className="text-gray-600">Deep sleep duration has increased by 15% over the past week.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SleepAnalysis; 